import type { BlocAllowedIn, BlocDefinition, SlotDefinition } from './bloc-definition';
import type { BlocCategory } from './bloc-categories';
import type { IBlockDescriptor } from './descriptor';
import { createBlockConfigResolver } from './block-config-resolver';
import type { ResolveBlockConfigRuntimeInput } from './block-config-resolver';
import type { ModalDefinition } from './modal-definition';
import {
  type DashboardLayoutFactoryInput,
  type RequiredDescriptorControlValues,
  type RequiredPropValues,
  type ResolvedConfig,
  type SharedFieldAccessors,
  type SharedFieldsDefinition,
  buildLegacyDescriptor,
  buildOverrideConfig,
  buildPrefixedExports,
  buildPropertyGroupsFromConfig,
  buildResolvedConfig,
  buildSharedAccessors,
  resolveDashboardLayout,
  type DashboardLayout,
  type OverrideConfig,
  type DoodBlockScenarioFactory,
  EMPTY_SCENARIO_FACTORY,
  type DefinitionSetupScenariosFn,
} from './dood-config-core';
import { createComposeConfigFn, type ComposeConfigFn } from './dood-block-config';

/* -------------------------------------------------------------------------- */
/* Modal metadata                                                             */
/* -------------------------------------------------------------------------- */

export type DoodModalMetadata<
  SharedFields extends SharedFieldsDefinition,
  Name extends string = string,
> = Omit<IBlockDescriptor, 'fields' | 'name'> & {
  name: Name;
  /** Exact Angular component class name, e.g. `AuthenticationModalComponent`. */
  componentType: string;
  category: BlocCategory;
  description?: string;
  keywords: readonly string[];
  sharedFields: SharedFields;
  /** CB2 slots; projected to `block_collection` fields in CB1. */
  slots?: readonly SlotDefinition[];
  isContentModal?: true;
  /** Default journey handle, e.g. `authentication-modal`. */
  defaultHandle?: string;
  allowedIn?: readonly BlocAllowedIn[];
};

/* -------------------------------------------------------------------------- */
/* Definition result                                                          */
/* -------------------------------------------------------------------------- */

export type DoodModalDefinition<Config = Record<string, unknown>> = {
  modalDescriptor: IBlockDescriptor;
  /** CB2 projection — fully serializable. */
  modalDefinition: ModalDefinition;
  properties: Config;
  config: ResolvedConfig<Config>;
  configToOverride: OverrideConfig<Config>;
  resolveConfig: (
    input: ResolveBlockConfigRuntimeInput<ResolvedConfig<Config>>,
  ) => ResolvedConfig<Config>;
  dashboardLayout: DashboardLayout<Config>;
  scenarios: DoodBlockScenarioFactory;
};

type ComponentTypePrefix<ComponentType extends string> =
  ComponentType extends `${infer Base}Component`
    ? Uncapitalize<Base>
    : Uncapitalize<ComponentType>;

export type PrefixedDoodModalDefinition<
  Config,
  ComponentType extends string,
> = {
  [Key in `${ComponentTypePrefix<ComponentType>}Descriptor`]: IBlockDescriptor;
} & {
  [Key in `${ComponentTypePrefix<ComponentType>}ModalDefinition`]: ModalDefinition;
} & {
  [Key in `${ComponentTypePrefix<ComponentType>}Config`]: ResolvedConfig<Config>;
} & {
  [Key in `compose${Capitalize<Uncapitalize<ComponentType extends `${infer Base}Component` ? Base : ComponentType>>}Config`]: ComposeConfigFn<
    Config & Record<string, unknown>,
    {}
  >;
} & {
  [Key in `${ComponentTypePrefix<ComponentType>}Properties`]: Config;
} & {
  [Key in `${ComponentTypePrefix<ComponentType>}DashboardLayout`]: DashboardLayout<Config>;
} & {
  [Key in `${ComponentTypePrefix<ComponentType>}Scenarios`]: DoodBlockScenarioFactory;
} & {
  [Key in `${ComponentTypePrefix<ComponentType>}DefinitionSetupScenarios`]: DefinitionSetupScenariosFn<ComponentType>;
} & {
  [Key in `${ComponentTypePrefix<ComponentType>}ResolveConfig`]: (
    input: ResolveBlockConfigRuntimeInput<ResolvedConfig<Config>>,
  ) => ResolvedConfig<Config>;
};

function buildModalDefinition<
  SharedFields extends SharedFieldsDefinition,
  Name extends string,
  Config,
>(
  metadata: DoodModalMetadata<SharedFields, Name>,
  config: Config,
): ModalDefinition {
  const propertyGroups = buildPropertyGroupsFromConfig(config);
  const definition: ModalDefinition = {
    type: metadata.componentType,
    name: metadata.name,
    category: metadata.category,
    propertyGroups,
  };
  if (metadata.description) definition.description = metadata.description;
  if (metadata.keywords?.length) definition.keywords = metadata.keywords;
  if (metadata.slots?.length) definition.slots = metadata.slots;
  if (metadata.isContentModal) definition.isContentModal = true;
  if (metadata.defaultHandle) definition.defaultHandle = metadata.defaultHandle;
  return definition;
}

/* -------------------------------------------------------------------------- */
/* doodModalConfig                                                            */
/* -------------------------------------------------------------------------- */

export function doodModalConfig<
  const SharedFields extends SharedFieldsDefinition,
  const Name extends string,
  const ComponentType extends string,
  const Config extends RequiredPropValues<SharedFields> &
    RequiredDescriptorControlValues<SharedFields>,
  const Layout extends DashboardLayoutFactoryInput<Config>,
>(
  metadata: DoodModalMetadata<SharedFields, Name> & { readonly componentType: ComponentType },
  configFactory: (shared: SharedFieldAccessors<SharedFields>) => Config,
  dashboardLayoutFactory: () => Layout,
): DoodModalDefinition<Config> & PrefixedDoodModalDefinition<Config, ComponentType> {
  const shared = buildSharedAccessors(metadata.sharedFields);
  const config = configFactory(shared);
  const modalDescriptor = buildLegacyDescriptor(
    {
      ...metadata,
      slots: metadata.slots,
      allowedIn: metadata.allowedIn,
    },
    config,
  );
  const modalDefinition = buildModalDefinition(metadata, config);
  const resolved = buildResolvedConfig(config);
  const override = buildOverrideConfig(config);
  const dashboardLayout = resolveDashboardLayout(
    dashboardLayoutFactory() as DashboardLayoutFactoryInput<Config>,
    config,
  );
  // Compat empty factory — real fixtures live in preview-scenarios sidecars.
  const scenarios = EMPTY_SCENARIO_FACTORY;
  const resolverDefinition = {
    properties: config,
    config: resolved,
    configToOverride: override as Partial<ResolvedConfig<Config>>,
  };
  const composeConfig = createComposeConfigFn(
    config as Record<string, unknown>,
  ) as ComposeConfigFn<Config & Record<string, unknown>, {}>;
  const definition: DoodModalDefinition<Config> = {
    modalDescriptor,
    modalDefinition,
    properties: config,
    config: resolved,
    configToOverride: override,
    resolveConfig: createBlockConfigResolver(resolverDefinition),
    dashboardLayout,
    scenarios,
  };
  return {
    ...definition,
    ...buildPrefixedExports(metadata.componentType, definition, composeConfig, 'modal'),
  } as DoodModalDefinition<Config> & PrefixedDoodModalDefinition<Config, ComponentType>;
}

/** Maps a modal definition to a bloc definition for shared inspector utilities. */
export function modalDefinitionToBlocDefinition(modal: ModalDefinition): BlocDefinition {
  return {
    type: modal.type,
    name: modal.name,
    category: modal.category,
    description: modal.description,
    keywords: modal.keywords,
    propertyGroups: modal.propertyGroups,
    slots: modal.slots,
    isModalRoot: true,
  };
}
